import React, { useEffect, useRef, useState } from "react";
import Modal from "../Modal";
import InputBox from "../../Input/Input";
import Button from "../../Button/Button";
import "./AuctionModal.css";
import {
  GetPaymentMethod,
  RemovePaymentMethod,
} from "../../../services/payment.service";
import useAuthService from "../../../auth/AuthService";
import {
  IBillingResponse,
  IPaymentMethodsResponse,
  IPlaceBid,
} from "../../../types/types";

import AddCard from "../../Stripe/AddCard/AddCard";
import { handleMessage } from "../../../utils/handleMessagePopup";
import { GetTax, PlaceBid } from "../../../services/auction.service";
import Swal from "sweetalert2";
import { GetBillingAddress } from "../../../services/billingDetails.service";
import { getCardLogo } from "../../../utils/cardLogoUtility";

const AuctionModal: React.FC<{
  isVisible?: boolean;
  auctionData?: any;
  onClose?: () => void;
}> = ({ isVisible, auctionData, onClose }) => {
  const { getToken } = useAuthService();
  const [state, setState] = useState({
    defaultCard: {} as IPaymentMethodsResponse,
    billingDetails: {} as IBillingResponse,
    value: auctionData.min_bid_price,
    MinBid: auctionData.min_bid_price,
    error: "",
    tax: 0,
    isLoading: false,
    isNewCard: false,
    isUpdateCard: false,
    isTaxLoading: false,
    countdown: {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    },
  });
  const countdownInterval = useRef<NodeJS.Timeout | null>(null);

  const updateCountdown = () => {
    const endTime = new Date(auctionData?.endtime);
    const now = new Date();
    const timeDiff = endTime.getTime() - now.getTime();

    if (timeDiff <= 0) {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
      return;
    }

    const days = Math.floor(timeDiff / (1000 * 3600 * 24));
    const hours = Math.floor((timeDiff % (1000 * 3600 * 24)) / (1000 * 3600));
    const minutes = Math.floor((timeDiff % (1000 * 3600)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    setState((prevState) => ({
      ...prevState,
      countdown: { days, hours, minutes, seconds },
    }));
  };

  useEffect(() => {
    countdownInterval.current = setInterval(updateCountdown, 1000);
    updateCountdown();

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionData?.endtime]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9.]/g, ""); // Remove non-numeric characters except dot

    if (val) {
      const numericVal = parseFloat(val);
      if (numericVal > 999999.99) {
        val = "999999.99"; // Ensure the max value is 999999.99
      }
    }

    const numericVal = parseFloat(val);

    // Update state with the new value
    setState((prev) => ({
      ...prev,
      value: numericVal,
    }));

    // Reset tax when bid value is empty or invalid
    if (
      !state.defaultCard?.payment_method_id ||
      numericVal === 0 ||
      numericVal > 999999.99
    ) {
      setState((prev) => ({
        ...prev,
        tax: 0,
      }));
      return;
    }

    // Fetch the tax if the value is valid
    const data: any = await CalculateSaleTax(
      numericVal,
      state.defaultCard.payment_method_id
    );
    setState((prevState) => ({
      ...prevState,
      tax: data.tax,
      error: data.error,
    }));
  };

  const toggleModal = (key: keyof typeof state) => {
    setState((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        if (!state.isUpdateCard || !state.isNewCard) {
          const res: any = await GetPaymentMethod(getToken);
          if (res.data.status_code === 200) {
            if (res.data.data === null) {
              const billingResponse: any = await GetBillingAddress(getToken);
              if (billingResponse.data.status_code === 200) {
                setState((prev) => ({
                  ...prev,
                  billingDetails: billingResponse.data.data,
                }));
              }
            } else {
              const defaultCard = res.data.data.find(
                (card: IPaymentMethodsResponse) => card.is_default === true
              );
              if (!defaultCard) {
                const billingResponse: any = await GetBillingAddress(getToken);
                if (billingResponse.data.status_code === 200) {
                  setState((prev) => ({
                    ...prev,
                    billingDetails: billingResponse.data.data,
                  }));
                }
              } else {
                const data: any = await CalculateSaleTax(
                  state.value,
                  defaultCard.payment_method_id
                );
                setState((prevState) => ({
                  ...prevState,
                  defaultCard: defaultCard,
                  tax: data.tax,
                  error: data.error,
                }));
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      }
    };

    fetchPaymentMethods();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isUpdateCard, state.isNewCard]);

  const CalculateSaleTax = async (val?: number, paymentMethodId?: string) => {
    try {
      toggleModal("isTaxLoading");

      const res = await GetTax(getToken, {
        amount: val || state.value,
        paymentMethodId: paymentMethodId || state.defaultCard.payment_method_id,
      });

      if (res.data != null) {
        return { tax: Number(res.data.tax), error: "" };
      } else {
        return { tax: 0, error: res.error };
      }
    } catch (error) {
      console.error("Error fetching tax:", error);
      return { tax: 0, error: "Unexpected error" };
    } finally {
      toggleModal("isTaxLoading");
    }
  };

  const handleBidSubmission = async () => {
    try {
      toggleModal("isLoading");

      if (!state.defaultCard?.payment_method_id) {
        setState((prev) => ({
          ...prev,
          error: "Please add a payment method before placing your bid.",
        }));
        return;
      }

      if (state.value < state.MinBid || isNaN(state.value)) {
        setState((prev) => ({
          ...prev,
          value: prev.MinBid,
          error: "Can't Place Bid less than minimum bid amount.",
        }));
        return;
      }

      const data: IPlaceBid = {
        entity_id: auctionData.id,
        bid_amount: Number(state.value),
        payment_method: state.defaultCard.payment_method_id,
      };

      const response = await PlaceBid(getToken, data);
      if (response.error) {
        setState((prev) => ({ ...prev, error: response.error as string }));
        return;
      }

      handleMessage(response.data.message);
      if (onClose) onClose();
    } catch (error) {
      console.error("An error occurred while placing bid", error);
      setState((prev) => ({
        ...prev,
        error: "An error occurred. Please try again.",
      }));
    } finally {
      toggleModal("isLoading");
    }
  };

  const handleClose = () => {
    if (onClose) {
      setState((prev) => ({ ...prev, error: "", value: 0 }));
      onClose();
    }
  };

  const handleRemoveCard = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      showCloseButton: true,
      showCancelButton: true,
      reverseButtons: true,
      showConfirmButton: true,
      width: 500,
      confirmButtonText: "Yes, Remove Card",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "removeCard",
        confirmButton: "confirm-btn alert-btn",
        cancelButton: "cancel-btn alert-btn",
      },
      color: "#ffffff",
      timer: 100000,
    }).then(async (result) => {
      if (result.isConfirmed) {
        toggleModal("isLoading");
        const res: any = await RemovePaymentMethod(
          {
            payment_method_id: id,
          },
          getToken
        );
        if (res.data?.status_code === 200) {
          setState((prev) => ({
            ...prev,
            value: 0,
            tax: 0,
            defaultCard: {} as IPaymentMethodsResponse,
          }));
          handleMessage("Payment Method Successfully Removed");
        } else {
          handleMessage("❌ Error in Removing Payment Method");
        }
        toggleModal("isLoading");
      }
    });
  };

  const modalBody = (
    <>
      {state.isLoading && (
        <div className="loader">
          <div className="spinner"></div>
        </div>
      )}
      <div className="modal-body bg-modalbody m-3 pt-0 my-0 px-5">
        <div className="mt-4">
          <div className="d-flex px-2 pb-4">
            <img
              src={auctionData.image}
              alt={auctionData.name}
              style={{ width: "60px", height: "60px", borderRadius: "4px" }}
              className="col-1 p-0"
            />
            <div className="text-start ms-4 w-100">
              <div className="auction-head1">{auctionData?.name}</div>
              <div className="d-flex justify-content-between mt-2">
                <div className="auction-title">Auction Ends: </div>
                <div className="auction-title font-400 text-light-purple">
                  {`${state.countdown.days}d ${state.countdown.hours}h ${state.countdown.minutes}m ${state.countdown.seconds}s`}
                </div>
              </div>
              <div className="d-flex justify-content-between mt-2">
                <div className="auction-title">Membership Term:</div>
                <div className="auction-title font-400 text-light-purple">
                  30 days
                </div>
              </div>
              <div className="d-flex justify-content-between mt-2">
                <div className="auction-title">Membership Expires: </div>
                <div className="auction-title font-400 text-light-purple">
                  {new Date(
                    Date.now() + 30 * 24 * 60 * 60 * 1000
                  ).toLocaleDateString("en-US")}
                </div>
              </div>
            </div>
          </div>

          <div className="auction-border auction-center py-4">
            <div className="auction-head2">Enter your bid</div>
            <InputBox
              type="text"
              value={state.value ? `$${state.value.toLocaleString("us")}` : ""}
              onChange={handleChange}
              className="my-3 auction-input text-dark-electric-purple"
            />
            <div className="auction-mid-footer d-flex">
              Estimated Sales Tax:{" "}
              <span className="text-extra-light-purple ms-2">
                {state.isTaxLoading ? (
                  <div className="inside-loader">
                    <div className="spinner"></div>
                  </div>
                ) : state.value ? (
                  `$${state.tax.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                ) : (
                  "$0"
                )}
              </span>
            </div>
          </div>

          <div className="py-4 auction-center">
            <div className="auction-head2">Payment Method</div>
            {Object.keys(state.defaultCard).length === 0 ? (
              <Button
                label="Add Payment Method"
                className="mt-4 px-5 mb-3 auction-btn font-500"
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    isNewCard: !prev.isNewCard,
                  }))
                }
              />
            ) : (
              <div className="my-3 p-2 auction-default-pay d-flex justify-content-between align-items-center">

                <img src={getCardLogo(state.defaultCard.brand)} alt={state.defaultCard.brand} className="pay-logo m-0" />

                <div className="ms-2 me-3">
                  <div className="auction-card">{state.defaultCard.brand.charAt(0).toUpperCase() + state.defaultCard.brand.slice(1)}</div>
                  <div className="auction-card-number">
                    **** **** **** {state.defaultCard.last4}
                  </div>
                </div>
                <div className="ms-5 text-end">
                  <div className="auction-default-border px-1">default</div>
                  <i
                    className="fa-solid fa-trash me-1"
                    onClick={() =>
                      handleRemoveCard(state.defaultCard.payment_method_id)
                    }
                  ></i>
                </div>
              </div>
            )}

            {state.error && <div className="error-msg">{state.error}</div>}
            {Object.keys(state.defaultCard).length === 0 && (
              <div className="error-msg">
                Please add a payment method to place your bid.
              </div>
            )}

            <Button
              label={
                state.isTaxLoading ? (
                  <div className="inside-loader">
                    <div className="spinner"></div>
                  </div>
                ) : (
                  "Place Bid"
                )
              }
              className={`w-100 mt-4 auction-btn font-500 ${Object.keys(state.defaultCard).length === 0 && "disable-btn"
                }`}
              disabled={state.isTaxLoading}
              onClick={handleBidSubmission}
            />

            <h6 className="auction-footer pt-4">
              By submitting a Bid, you authorize Fan1 to automatically charge
              your{" "}
              {state.defaultCard.brand
                ? state.defaultCard.brand
                : "payment method"}{" "}
              when the auction ends if you are the highest bidder. You also
              agree to the Fan1{" "}
              <a href="terms" className="txtcolor">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="privacy" className="txtcolor">
                Privacy Notice
              </a>
              .
            </h6>
          </div>
        </div>
      </div>

      <AddCard
        title="Add Card"
        id="AddCardModal"
        isAuction={true}
        billing_Details={state.billingDetails}
        onClick={() => {
          setState((prev) => ({ ...prev, isNewCard: !prev.isNewCard }));
          handleMessage("Payment Method Successfully Added");
        }}
        onClose={() =>
          setState((prev) => ({ ...prev, isNewCard: !prev.isNewCard }))
        }
        isVisible={state.isNewCard}
      />
    </>
  );

  return (
    <Modal
      id="AuctionModal"
      title="Bid Now"
      body={modalBody}
      modalBackground={"modal-bg"}
      onClose={handleClose}
      isVisible={isVisible}
    />
  );
};

export default AuctionModal;
